import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import {
	MomentInstance,
	MomentSkipReason,
	MomentTemplate,
	SkipMomentInstanceInput,
	SkipMomentTemplateInput,
	useSkipMomentInstanceMutation,
	useSkipMomentTemplateMutation,
} from '@treams/treams-network';
import {
	GraphQLErrorMessage,
	Input,
	Label,
	Modal,
	Paragraph,
	Popover,
	Select,
	Spinner,
	Subheading,
} from '@treams/treams-ui';

export type CycleMomentInstance = {
	cycleInstanceID: string;
	instance?: MomentInstance;
	template: MomentTemplate;
};

type CycleMomentSkipModalProps = {
	moments: CycleMomentInstance[];
	onDismiss: (userId?: string) => void;
};

/**
 * Conditional type to determine if it's an moment instance or template
 * @template T - The type to conditionally extend.
 */
type InputType<T> = T extends MomentInstance ? SkipMomentInstanceInput : SkipMomentTemplateInput;

/**
 * Modal component for handling the skipping of one or more moment cycles
 * Generates reason selection boxes for every given moment
 */
export default ({ moments, onDismiss }: CycleMomentSkipModalProps) => {
	const { t } = useTranslation('', { keyPrefix: 'inbox.cycle.moments.skipModal' });
	const { localize } = useLocalize();

	const [commitInstance, { loading: loadingInstance, error: errorInstance }] =
		useSkipMomentInstanceMutation();
	const [commitTemplate, { loading: loadingTemplate, error: errorTemplate }] =
		useSkipMomentTemplateMutation();

	const initialValues = {
		moments: moments.map((moment, idx) => {
			return {
				index: idx,
				moment: moment,
				reason: undefined as MomentSkipReason | undefined,
				reasonOther: '',
			};
		}),
	};

	const formik = useFormik({
		initialValues,
		onSubmit: (values) => handleOnSubmit(values.moments),
	});

	const allMomentsValid = formik.values.moments.every(({ reason, reasonOther }) => {
		return reason && (reason !== MomentSkipReason.Other || reasonOther.length !== 0);
	});

	const isMultipleMoments = moments.length < 2;

	const headerTitle = isMultipleMoments
		? localize((moments[0].instance?.momentTemplate ?? moments[0].template).title).text
		: t('multipleMoments.title');

	const handleOnSubmit = async (values: typeof initialValues.moments) => {
		if (values) {
			for (const value of values) {
				const {
					reason,
					reasonOther,
					moment: { instance, template, cycleInstanceID },
				} = value;

				const valid = reason && (reason !== MomentSkipReason.Other || reasonOther.length !== 0);

				if (!valid || loadingInstance || loadingTemplate) {
					return;
				}

				const baseInput = {
					skipReason: reason as MomentSkipReason,
					skipReasonOther: (reason === MomentSkipReason.Other && reasonOther) || undefined,
				};

				let input: InputType<typeof instance>;

				if (instance) {
					input = { id: instance.id, ...baseInput };

					await commitInstance({ variables: { input } });
				} else {
					input = {
						cycleInstanceID,
						momentTemplateID: template.id,
						...baseInput,
					};

					await commitTemplate({ variables: { input } });
				}
			}
		}
		onDismiss(moments[0].instance?.user.id);
	};

	return (
		<Popover center visible>
			<Modal>
				<Modal.Header title={headerTitle} onClose={() => onDismiss()} />
				{loadingInstance || loadingTemplate ? (
					<Spinner centered />
				) : (
					<FormikProvider value={formik}>
						<Modal.Contents>
							{formik.values.moments.map((moment) => (
								<Fragment key={moment.moment.instance?.id}>
									{!isMultipleMoments && (
										<Subheading>
											{moment.moment.instance
												? localize(moment.moment.instance.momentTemplate.title).text
												: localize(moment.moment.template.title).text}
										</Subheading>
									)}
									<Paragraph>{t('text')}</Paragraph>
									<Box mt={3}>
										<Label mb={2}>{t('reason.label')}</Label>
										<Flex>
											<CycleMomentSkipReasonSelect moment={moment} />
										</Flex>
									</Box>

									{moment.reason === MomentSkipReason.Other && (
										<Box mt={3}>
											<Label mb={2}>{t('reasonOther.label')}</Label>
											<Input.Formik name={`moments.${moment.index}.reasonOther`} />
										</Box>
									)}
								</Fragment>
							))}
							<GraphQLErrorMessage error={(errorInstance ?? errorTemplate)?.graphQLErrors[0]} />
						</Modal.Contents>

						<Modal.FormFooter
							disabled={!allMomentsValid}
							onSave={() => formik.submitForm()}
							saveTitle={t('skipButton')}
							onCancel={() => onDismiss()}
						/>
					</FormikProvider>
				)}
			</Modal>
		</Popover>
	);
};

function CycleMomentSkipReasonSelect({ moment }) {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext();

	const options = [
		{
			title: t('inbox.cycle.moments.skipModal.reason.duplicate'),
			value: MomentSkipReason.Duplicate,
		},
		{
			title: t('inbox.cycle.moments.skipModal.reason.leave'),
			value: MomentSkipReason.Leave,
		},
		{
			title: t('inbox.cycle.moments.skipModal.reason.illness'),
			value: MomentSkipReason.Illness,
		},
		{
			title: t('inbox.cycle.moments.skipModal.reason.other'),
			value: MomentSkipReason.Other,
		},
	];

	return (
		<Select
			selectedValue={(values as any).moments[moment.index].reason}
			options={options}
			onChange={(option) => setFieldValue(`moments.${moment.index}.reason`, option)}
			search={false}
			placeholder={t('inbox.cycle.moments.skipModal.reason.placeholder')}
		/>
	);
}
